import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Mic, MicOff, X, Trash2, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Botão flutuante + janela de chat com a IA do salão.
 * - Microfone usa Web Speech API (pt-BR), funciona em Chrome Android/iOS Safari.
 * - Envia para /api/assistant/chat e executa action retornada (open_route, etc).
 */
export default function AssistantWidget() {
  const { apiCall, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);
  const sessionId = `sess_${user?.user_id || "guest"}`;

  // Auto-abre o assistente se a URL pedir (?assistente=1) - usado pelos atalhos PWA / Siri / Google
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("assistente") === "1") {
      setOpen(true);
      // limpa o param para não reabrir
      params.delete("assistente");
      const newUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const supportsSpeech = typeof window !== "undefined" && (
    "webkitSpeechRecognition" in window || "SpeechRecognition" in window
  );

  // Carrega histórico ao abrir pela primeira vez
  const loadHistory = useCallback(async () => {
    try {
      const res = await apiCall("get", `/assistant/history?session_id=${sessionId}`);
      const items = (res.data || []).map((m) => ({
        role: m.role,
        text: m.content,
        action: m.action,
        action_result: m.action_result,
      }));
      setMessages(items);
    } catch (e) {
      // primeiro uso
    }
  }, [apiCall, sessionId]);

  useEffect(() => {
    if (open && messages.length === 0) loadHistory();
  }, [open, loadHistory, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setSending(true);  // bloqueia duplo-clique imediatamente
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");

    // 1) PRIMEIRO: tenta a base local Gregory AI (sem custo de IA)
    const looksLikeTechnical = /\b(cabelo|fio|fios|raiz|couro|cacho|crespo|liso|cor|tom|matiz|loiro|ruivo|moreno|cobre|amarel|laranj|elast|emborrach|hidrata|reconstru|nutri|botox|progressi|alisa|mecha|babylight|balay|corte|barba|alergi|caspa|queda|alopec|química|quimica|formol|tioglic|peróxido|volume|oxigenada|escova|química|protocol)/i.test(msg);
    if (looksLikeTechnical) {
      try {
        const res = await apiCall("get", `/knowledge/search?q=${encodeURIComponent(msg)}&top_k=3`);
        const d = res.data;
        if (d?.top_confidence?.level === "green" || d?.top_confidence?.level === "yellow") {
          // Resposta local — sem custo
          const top = d.results[0];
          setMessages((m) => [...m, {
            role: "assistant",
            text: `${d.top_confidence.icon} ${d.top_confidence.label} (${d.top_confidence.pct}%)\n\n**${top.article.title}**\n\n${top.article.content}`,
            source: "gregory_ai",
            confidence: d.top_confidence,
          }]);
          setSending(false);
          return;
        }
      } catch {}
    }

    // 2) Senão: usa IA (com custo)
    try {
      const res = await apiCall("post", "/assistant/chat", { message: msg, session_id: sessionId });
      const reply = res.data || {};
      setMessages((m) => [...m, { role: "assistant", text: reply.message || "…", action: reply.action, action_result: reply.action_result, source: "llm" }]);
      if (reply.action?.type === "open_route" && reply.action_result?.navigate) {
        setTimeout(() => navigate(reply.action_result.navigate), 600);
      }
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Erro ao falar com o assistente. Tenta de novo?" }]);
    } finally {
      setSending(false);
    }
  };

  const startListening = () => {
    if (!supportsSpeech) {
      toast.error("Este navegador não suporta voz. Use Chrome ou Safari.");
      return;
    }
    try {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const rec = new SR();
      rec.lang = "pt-BR";
      rec.interimResults = true;
      rec.continuous = false;
      let finalText = "";
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) finalText += t;
          else interim += t;
        }
        setInput(finalText || interim);
      };
      rec.onerror = () => setListening(false);
      rec.onend = () => {
        setListening(false);
        if (finalText.trim()) send(finalText.trim());
      };
      recognitionRef.current = rec;
      rec.start();
      setListening(true);
    } catch (err) {
      setListening(false);
    }
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
  };

  const clearHistory = async () => {
    if (!window.confirm("Limpar todo o histórico do assistente?")) return;
    try {
      await apiCall("delete", `/assistant/history?session_id=${sessionId}`);
      setMessages([]);
      toast.success("Histórico apagado");
    } catch {
      toast.error("Erro ao limpar");
    }
  };

  // Sugestões de comandos rápidos
  const QUICK_PROMPTS = [
    "Qual é a agenda de hoje?",
    "Quanto faturei essa semana?",
    "Boletos a vencer essa semana",
    "Clientes que não vêm há 60 dias",
    "Buscar cliente Maria",
  ];

  if (!user) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-[60] h-14 w-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-110 active:scale-95 sm:bottom-20 sm:right-6"
          style={{
            background: "linear-gradient(135deg, #CBA871 0%, #8B5CF6 100%)",
          }}
          data-testid="assistant-fab"
          aria-label="Abrir assistente"
        >
          <Sparkles className="h-6 w-6 text-white" strokeWidth={1.8} />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-4 right-4 z-[60] w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-2rem))] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-white/30 sm:bottom-20 sm:right-6"
          style={{
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(28px) saturate(180%)",
          }}
          data-testid="assistant-panel"
        >
          {/* Header */}
          <div
            className="px-4 py-3 flex items-center justify-between text-white"
            style={{ background: "linear-gradient(135deg, #CBA871 0%, #8B5CF6 100%)" }}
          >
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" strokeWidth={1.8} />
              <div>
                <div className="text-sm font-medium tracking-tight">Assistente Gregory</div>
                <div className="text-[10px] opacity-80">Pergunte ou peça em voz alta</div>
              </div>
            </div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-white/15 rounded-full" onClick={clearHistory} title="Limpar conversa" data-testid="assistant-clear">
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-white/15 rounded-full" onClick={() => setOpen(false)} data-testid="assistant-close">
                <X className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="space-y-3 pt-2">
                <div className="text-xs text-muted-foreground text-center">
                  Oi {user.name?.split(" ")[0]}! Posso te ajudar com:
                </div>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {QUICK_PROMPTS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => send(q)}
                      className="text-[11px] px-2 py-1 rounded-full bg-secondary/70 hover:bg-amber-100 border border-border transition"
                      data-testid={`assistant-quick-${q.slice(0, 15)}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, idx) => (
              <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                    m.role === "user"
                      ? "bg-foreground text-background rounded-br-sm"
                      : "bg-white border border-border rounded-bl-sm"
                  }`}
                  data-testid={`assistant-msg-${idx}`}
                >
                  {m.source === "gregory_ai" && (
                    <div className="text-[9px] uppercase tracking-widest text-green-700 mb-1 font-medium" data-testid="msg-source-gregory">
                      📚 Gregory AI · sem custo
                    </div>
                  )}
                  {m.source === "llm" && m.role === "assistant" && (
                    <div className="text-[9px] uppercase tracking-widest text-purple-700 mb-1 font-medium" data-testid="msg-source-llm">
                      🤖 IA · centavos
                    </div>
                  )}
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  {m.action_result && Array.isArray(m.action_result?.appointments) && m.action_result.appointments.length > 0 && (
                    <div className="mt-2 space-y-1 pt-2 border-t border-border/40">
                      {m.action_result.appointments.slice(0, 8).map((a, i) => (
                        <div key={i} className="text-[11px] text-muted-foreground">
                          <span className="font-medium text-foreground">{a.horario}</span> · {a.cliente} {a.profissional ? `· ${a.profissional}` : ""} {a.servicos?.[0] ? `· ${a.servicos[0]}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                  {m.action_result && Array.isArray(m.action_result?.clientes) && m.action_result.clientes.length > 0 && (
                    <div className="mt-2 space-y-1 pt-2 border-t border-border/40">
                      {m.action_result.clientes.slice(0, 10).map((c, i) => (
                        <div key={i} className="text-[11px] text-muted-foreground">
                          <span className="font-medium text-foreground">{c.nome}</span> · {c.dias_sem_vir} dias
                        </div>
                      ))}
                    </div>
                  )}
                  {m.action_result?.total !== undefined && (
                    <div className="mt-2 text-base font-medium">
                      {Number(m.action_result.total).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} <span className="text-[10px] text-muted-foreground">· {m.action_result.lancamentos} lançamentos</span>
                    </div>
                  )}
                  {m.action_result?.boletos?.length > 0 && (
                    <div className="mt-2 space-y-1 pt-2 border-t border-border/40">
                      {m.action_result.boletos.slice(0, 10).map((b, i) => (
                        <div key={i} className="text-[11px] text-muted-foreground">
                          <span className="font-medium text-foreground">{b.vence}</span> · {b.descricao} · R$ {Number(b.valor).toFixed(2)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-3 py-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" strokeWidth={1.5} />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-2 border-t border-border bg-white/50 flex items-center gap-1">
            <Button
              type="button"
              size="icon"
              variant={listening ? "default" : "ghost"}
              className="h-9 w-9 rounded-full shrink-0"
              onClick={listening ? stopListening : startListening}
              data-testid="assistant-mic"
              title={listening ? "Parar de gravar" : "Falar"}
            >
              {listening ? <MicOff className="h-4 w-4 animate-pulse" strokeWidth={1.5} /> : <Mic className="h-4 w-4" strokeWidth={1.5} />}
            </Button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder={listening ? "Escutando…" : "Digite ou fale"}
              className="flex-1 bg-transparent outline-none px-2 py-2 text-sm"
              data-testid="assistant-input"
              disabled={sending}
            />
            <Button
              type="button"
              size="icon"
              className="h-9 w-9 rounded-full shrink-0"
              onClick={() => send()}
              disabled={!input.trim() || sending}
              data-testid="assistant-send"
            >
              <Send className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
